import { useState } from "react";
import dayjs from "dayjs";

import {
  faCalendarCheck,
  faRuler,
  faBolt,
  faThermometerHalf
} from "@fortawesome/free-solid-svg-icons";

import { letterSBoolean } from "@aclymatepackages/formatters";

import useCsvUploader from "./csvUploader";

import usePlatformLayoutContext from "../../helpers/contexts/platformLayout";
import {
  createErrorObj,
  doesFieldExist,
  useSharedFormLoading
} from "../../helpers/components/inputs";
import { analyticsTrack } from "../../helpers/analytics";
import {
  useAccountData,
  uploadFileToStorage,
  useMutateCachedCollection
} from "../../helpers/firebase";
import { fetchOurApi } from "../../helpers/utils/apiCalls";
import { getAccountId } from "../../helpers/accountHelpers";

const buildConfigForSubcategory = (subcategory) => {
  if (subcategory === "purchased-water") {
    return {
      fieldOptions: [
        { label: "Date", value: "dateCommon" },
        { label: "Quantity", value: "quantity" },
        { label: "Unit (gallons or cubicMeters)", value: "unit" },
        { label: "Wastewater %", value: "wastewaterPercentage" }
      ],
      requiredFields: [
        {
          label: "Date",
          description: "When was this water usage recorded?",
          icon: faCalendarCheck
        },
        {
          label: "Quantity",
          description: "How much water was used?",
          icon: faRuler
        }
      ],
      knownFields: [
        { header: "date", field: "dateCommon" },
        { header: "dateCommon", field: "dateCommon" },
        { header: "statement date", field: "dateCommon" },
        { header: "quantity", field: "quantity" },
        { header: "gallons", field: "quantity" },
        { header: "gallons/month", field: "quantity" },
        { header: "amount", field: "quantity" },
        { header: "unit", field: "unit" },
        { header: "units", field: "unit" },
        { header: "wastewater", field: "wastewaterPercentage" },
        { header: "wastewater percentage", field: "wastewaterPercentage" },
        { header: "wastewaterPercentage", field: "wastewaterPercentage" }
      ],
      fieldValidator: (headers) => {
        if (!doesFieldExist(headers, "dateCommon")) {
          return createErrorObj("Your CSV must include a Date field");
        }
        if (!doesFieldExist(headers, "quantity")) {
          return createErrorObj("Your CSV must include a Quantity field");
        }
        return { success: true };
      },
      firstStepText:
        "Upload a CSV of your purchased water usage and we'll calculate the emissions for each entry.",
      docType: "purchased-water",
      label: "Purchased Water"
    };
  }

  if (subcategory === "steam") {
    return {
      fieldOptions: [
        { label: "Date", value: "dateCommon" },
        { label: "Quantity", value: "quantity" },
        { label: "Unit (lbs, kgs, or tons)", value: "unit" }
      ],
      requiredFields: [
        {
          label: "Date",
          description: "When was this steam usage recorded?",
          icon: faCalendarCheck
        },
        {
          label: "Quantity",
          description: "How much steam was used?",
          icon: faRuler
        }
      ],
      knownFields: [
        { header: "date", field: "dateCommon" },
        { header: "dateCommon", field: "dateCommon" },
        { header: "statement date", field: "dateCommon" },
        { header: "quantity", field: "quantity" },
        { header: "lbs", field: "quantity" },
        { header: "amount", field: "quantity" },
        { header: "unit", field: "unit" },
        { header: "units", field: "unit" }
      ],
      fieldValidator: (headers) => {
        if (!doesFieldExist(headers, "dateCommon")) {
          return createErrorObj("Your CSV must include a Date field");
        }
        if (!doesFieldExist(headers, "quantity")) {
          return createErrorObj("Your CSV must include a Quantity field");
        }
        return { success: true };
      },
      firstStepText:
        "Upload a CSV of your steam usage and we'll calculate the emissions for each entry.",
      docType: "steam",
      label: "Steam"
    };
  }

  if (subcategory === "chill-water") {
    return {
      fieldOptions: [
        { label: "Date", value: "dateCommon" },
        { label: "kWh", value: "kwh" }
      ],
      requiredFields: [
        {
          label: "Date",
          description: "When was this chill water usage recorded?",
          icon: faCalendarCheck
        },
        {
          label: "kWh",
          description: "How many kWh did the chiller consume?",
          icon: faBolt
        }
      ],
      knownFields: [
        { header: "date", field: "dateCommon" },
        { header: "dateCommon", field: "dateCommon" },
        { header: "statement date", field: "dateCommon" },
        { header: "kwh", field: "kwh" },
        { header: "kWh", field: "kwh" },
        { header: "chillWaterKwh", field: "kwh" },
        { header: "chill water kwh", field: "kwh" }
      ],
      fieldValidator: (headers) => {
        if (!doesFieldExist(headers, "dateCommon")) {
          return createErrorObj("Your CSV must include a Date field");
        }
        if (!doesFieldExist(headers, "kwh")) {
          return createErrorObj("Your CSV must include a kWh field");
        }
        return { success: true };
      },
      firstStepText:
        "Upload a CSV of your chill water kWh usage and we'll calculate the emissions for each entry.",
      docType: "chill-water",
      label: "Chill Water"
    };
  }

  return {
    fieldOptions: [
      { label: "Date", value: "dateCommon" },
      { label: "Quantity", value: "quantity" },
      { label: "Unit (lbs, kgs, or tons)", value: "unit" },
      { label: "Refrigerant Type (e.g. R-410A, R-134a, R-407C)", value: "refrigerantType" }
    ],
    requiredFields: [
      {
        label: "Date",
        description: "When was this refrigerant usage recorded?",
        icon: faCalendarCheck
      },
      {
        label: "Refrigerant Type",
        description: "What type of refrigerant was used? (e.g. R-410A, R-134a, HFC-125, R-22, HFO-1234yf)",
        icon: faThermometerHalf
      },
      {
        label: "Quantity",
        description: "How much refrigerant was used?",
        icon: faRuler
      }
    ],
    knownFields: [
      { header: "date", field: "dateCommon" },
      { header: "dateCommon", field: "dateCommon" },
      { header: "statement date", field: "dateCommon" },
      { header: "quantity", field: "quantity" },
      { header: "quantity leaked", field: "quantity" },
      { header: "amount", field: "quantity" },
      { header: "unit", field: "unit" },
      { header: "units", field: "unit" },
      { header: "type", field: "refrigerantType" },
      { header: "refrigerant type", field: "refrigerantType" },
      { header: "refrigerantType", field: "refrigerantType" },
      { header: "refrigerant", field: "refrigerantType" },
      { header: "type of refrigerant", field: "refrigerantType" }
    ],
    fieldValidator: (headers) => {
      if (!doesFieldExist(headers, "dateCommon")) {
        return createErrorObj("Your CSV must include a Date field");
      }
      if (!doesFieldExist(headers, "quantity")) {
        return createErrorObj("Your CSV must include a Quantity field");
      }
      if (!doesFieldExist(headers, "refrigerantType")) {
        return createErrorObj("Your CSV must include a Refrigerant Type field");
      }
      return { success: true };
    },
    firstStepText:
      "Upload a CSV of your refrigerant usage and we'll calculate the emissions for each entry.",
    docType: "refrigerants",
    label: "Refrigerants"
  };
};

const useProcessOfficeCsv = ({
  fileInfo,
  setOpen,
  selectedOfficeId,
  selectedOfficeName,
  subcategory,
  setResponseCount,
  setTotalCount
}) => {
  const companyId = window.sessionStorage.getItem("accountId");
  const { name: fileName } = fileInfo;
  const { setFormLoading } = useSharedFormLoading();
  const [{ startDate, address }] = useAccountData();
  const mutateCachedCollection = useMutateCachedCollection();
  const { activateSnackbar } = usePlatformLayoutContext();
  const { label } = buildConfigForSubcategory(subcategory);

  return async ({ data: rows, originalFileObj, docType, hasUserConfirmedUpload }) => {
    setFormLoading(true);
    setTotalCount(rows.length);

    analyticsTrack("Office CSV Loaded", { subcategory, numberOfRows: rows.length });

    const accountId = getAccountId();
    const storagePath = `${accountId}/csv/${docType}/${dayjs().format(
      "MM-DD-YYYY--"
    )}${fileName.split(" ").join("_")}`;

    const uploadCsvToStorage = async () => {
      try {
        await uploadFileToStorage({
          path: `${storagePath}/raw-data.csv`,
          fileObject: originalFileObj,
          hasUserConfirmedUpload
        });
        return `${storagePath}/processed-data.csv`;
      } catch (e) {
        console.error("[CSV Upload] Storage upload failed, continuing without file storage:", e);
        return null;
      }
    };

    const filePath = originalFileObj ? await uploadCsvToStorage() : null;

    const rowsWithMetadata = rows.map((row) => ({
      ...row,
      source: "csv-upload",
      csvName: fileName,
      status: "confirmed",
      vendor: `${dayjs().format("MM/DD/YYYY")} CSV Upload ${label} Emissions`,
      name: `${dayjs().format("MM/DD/YYYY")} CSV Upload ${label} Emissions`,
      emissionProbability: "high"
    }));

    const result = await fetchOurApi({
      path: "/transactions/office-csv-sort",
      method: "POST",
      data: {
        companyId,
        subcategory,
        officeId: selectedOfficeId,
        officeName: selectedOfficeName,
        address,
        startDate,
        rows: rowsWithMetadata,
        filePath
      },
      callback: (response) => {
        setResponseCount(rows.length);
        return response;
      }
    });

    const { success, newTransactions = [] } = result || {};

    if (success && newTransactions.length) {
      mutateCachedCollection("transactions", (existingTransactions = []) => [
        ...existingTransactions,
        ...newTransactions
      ]);
    }

    const successCount = newTransactions.length;
    const errorCount = rows.length - successCount;
    const snackbarMessage = `${successCount} ${label.toLowerCase()} emission${letterSBoolean(
      newTransactions
    )} ${successCount === 1 ? "was" : "were"} added to your account${
      errorCount > 0 ? ` (${errorCount} row${letterSBoolean({ length: errorCount })} had errors — check Account Activity)` : ""
    }`;

    activateSnackbar({
      message: snackbarMessage,
      alert: success ? "success" : "error"
    });

    setOpen(null);
    return setFormLoading(false);
  };
};

const useOfficeCsvUploader = ({
  open,
  setOpen,
  selectedOfficeId,
  selectedOfficeName,
  subcategory
}) => {
  const [fileInfo, setFileInfo] = useState({});
  const [matchedHeaders, setMatchedHeaders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [responseCount, setResponseCount] = useState(0);

  const {
    fieldOptions,
    requiredFields,
    knownFields,
    fieldValidator,
    firstStepText,
    docType
  } = buildConfigForSubcategory(subcategory);

  const processData = useProcessOfficeCsv({
    fileInfo,
    setOpen,
    selectedOfficeId,
    selectedOfficeName,
    subcategory,
    setResponseCount,
    setTotalCount
  });

  const csvUploaderSteps = useCsvUploader({
    knownFields,
    fieldOptions,
    open,
    setOpen,
    fileInfo,
    setFileInfo,
    matchedHeaders,
    setMatchedHeaders,
    processData,
    fieldValidator,
    docType,
    firstStepText,
    requiredFields
  });

  return [csvUploaderSteps, (responseCount / totalCount) * 100];
};

export default useOfficeCsvUploader;
